/**
 * Footer Component
 * Design: "Signal & Noise" – Dark Tech
 */
import { useLanguage } from "@/contexts/LanguageContext";

const platforms = [
  "Google Ads", "Meta Ads", "Facebook Ads", "Instagram Ads", "Shopify Ads", "TikTok Ads",
  "LinkedIn Ads", "YouTube Ads", "Microsoft Ads", "Pinterest Ads", "Snapchat Ads", "X Ads",
];

export default function Footer() {
  const { t, language } = useLanguage();
  const shopifyUrl = language === "en" ? "/shopify-meta-ads-agentur?lang=en" : "/shopify-meta-ads-agentur";
  const metaAdsDubaiUrl = language === "en" ? "/meta-ads-agentur-dubai?lang=en" : "/meta-ads-agentur-dubai";

  return (
    <footer className="relative border-t border-white/8" style={{ background: "#060A12" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center font-mono-custom font-bold tracking-[-0.08em] text-white text-xs">
                10X
              </div>
              <div>
                <div className="font-sora font-bold text-white text-lg leading-none">M.Sc. Cem Fernández-Yigit</div>
                <div className="text-[10px] text-blue-400 font-mono-custom tracking-widest uppercase">Performance Marketing</div>
              </div>
            </div>
            <p className="text-slate-300 text-sm font-inter leading-relaxed max-w-sm mb-4">
              {t.footer.tagline}
            </p>
            <div className="text-xs text-slate-300 font-mono-custom mb-5">
              Berlin, Deutschland · Dubai, UAE
            </div>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.linkedin.com/in/cem-fern%C3%A1ndez-yi%C4%9Fit-233248108/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-blue-600/20 border border-white/8 hover:border-blue-500/40 text-slate-300 hover:text-blue-300 transition-all duration-200 text-xs font-inter"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                LinkedIn
              </a>
              <a
                href="https://www.instagram.com/cemimpact"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-pink-600/20 border border-white/8 hover:border-pink-500/40 text-slate-300 hover:text-pink-300 transition-all duration-200 text-xs font-inter"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 100-2.881z" />
                </svg>
                Instagram
              </a>
            </div>
          </div>

          {/* Platforms */}
          <div>
            <h4 className="font-sora font-semibold text-white text-sm uppercase tracking-wider mb-4">
              {t.footer.platformsTitle}
            </h4>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5">
              {platforms.map((platform) => (
                <li key={platform}>
                  <span className="text-slate-300 text-xs font-inter">{platform}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 border-t border-white/8 pt-4">
              <p className="mb-2 text-[10px] font-sora font-semibold uppercase tracking-widest text-slate-400">
                {language === "en" ? "Service page" : "Leistungsseite"}
              </p>
              <div className="flex flex-col items-start gap-2">
                <a href={metaAdsDubaiUrl} className="text-xs font-inter text-slate-300 transition-colors hover:text-blue-300">
                  {language === "en" ? "German Meta Ads Agency Dubai" : "Deutsche Meta Ads Agentur Dubai"}
                </a>
                <a href={shopifyUrl} className="text-xs font-inter text-slate-300 transition-colors hover:text-blue-300">
                  {language === "en" ? "Shopify Meta Ads Agency" : "Shopify Meta Ads Agentur"}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright bar */}
        <div className="pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <p className="text-slate-300 text-sm font-inter">
            © 2026 Cem Fernández-Yigit, M.Sc. · Cem10X Marketing FZCO · {t.footer.copyright}
          </p>
          <a href="mailto:info@cem-yigit.de" className="inline-flex min-h-6 items-center text-slate-300 hover:text-blue-300 text-sm font-inter transition-colors">
            info@cem-yigit.de
          </a>
        </div>

        {/* Impressum */}
        <div className="border-t border-white/5 pt-6">
          <p className="text-slate-300 text-xs font-sora font-semibold uppercase tracking-widest mb-4">
            {t.footer.impressumTitle}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider font-sora mb-1">
                {language === "en" ? "Company" : "Unternehmen"}
              </p>
              <p className="text-slate-300 text-xs font-inter leading-relaxed">
                {t.footer.company}<br />
                {t.footer.address}<br />
                {t.footer.country}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider font-sora mb-1">
                {language === "en" ? "Responsible" : "Verantwortlich"}
              </p>
              <p className="text-slate-300 text-xs font-inter leading-relaxed">
                Cem Fernández-Yigit, M.Sc.
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider font-sora mb-1">
                {language === "en" ? "Contact" : "Kontakt"}
              </p>
              <p className="text-slate-300 text-xs font-inter leading-relaxed">
                <a href="mailto:info@cem-yigit.de" className="inline-flex min-h-6 items-center hover:text-white transition-colors">info@cem-yigit.de</a><br />
                <a href="https://www.cem-yigit.de" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-6 items-center hover:text-white transition-colors">www.cem-yigit.de</a>
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider font-sora mb-1">
                {language === "en" ? "Phone" : "Telefon"}
              </p>
              <p className="text-slate-300 text-xs font-inter leading-relaxed">
                UAE: +971 058 52 36 686<br />
                GER: +49 157 36 36 37 35
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
